import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Alert,
  Container,
  Stack,
  Button,
  Paper,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { getMe, MeResponse } from "../api/me";
import { ApiError } from "../api/client";
import heroArt from "../assets/archiv3-hero.png";

// Landing page after sign-in. Leads with the ARCHIV3 hero (navy field,
// 60% low-poly wordmark art / 40% product copy), then the per-user
// greeting + the two primary actions. /api/me also doubles as a quick
// smoke check that the auth round-trip is healthy.

function Hero() {
  return (
    <Box
      sx={{
        bgcolor: "background.brand",
        color: "common.white",
        borderRadius: 2,
        overflow: "hidden",
        mb: 4,
        px: { xs: 2.5, md: 4 },
        py: { xs: 2.5, md: 3 },
        textAlign: "center",
      }}
    >
      {/* The ARCHIV3 wordmark, full width. The PNG is white linework on a
          transparent background, so it reads as white-on-navy directly — and
          it IS the product name, so we don't repeat it as text below. */}
      <Box
        component="img"
        src={heroArt}
        alt="ARCHIV3"
        sx={{ width: "100%", height: "auto", display: "block", mx: "auto" }}
      />
      <Typography
        component="p"
        sx={{
          mt: { xs: 2.5, md: 3.5 },
          color: "rgba(255,255,255,0.7)",
          textTransform: "uppercase",
          fontWeight: 600,
          letterSpacing: "0.28em",
          fontSize: { xs: "0.75rem", md: "0.95rem" },
          // letter-spacing adds trailing space on the right; pad the left
          // by the same so the line stays visually centred.
          pl: "0.28em",
        }}
      >
        Index everything. Export Anywhere.
      </Typography>
    </Box>
  );
}

export function Home() {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getMe()
      .then(setMe)
      .catch((err: unknown) => {
        if (err instanceof ApiError) {
          setError(`API ${err.status}: ${JSON.stringify(err.body)}`);
        } else {
          setError(String(err));
        }
      });
  }, []);

  return (
    <Container sx={{ py: 4 }}>
      <Hero />

      {error && <Alert severity="error">{error}</Alert>}
      {!error && !me && <Typography>Loading…</Typography>}
      {!error && me && (
        <>
          <Box display="flex" gap={1} alignItems="center" mb={1}>
            <Typography variant="h6">Welcome, {me.name ?? me.sub}</Typography>
            <Chip
              label={me.role}
              color={me.role === "admin" ? "primary" : "default"}
              size="small"
            />
          </Box>
          {me.email && <Typography color="text.secondary">{me.email}</Typography>}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 4 }}>
            <Paper variant="outlined" sx={{ p: 3, flex: 1 }}>
              <Typography variant="h6">Create a Dataset</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                Walk through a five-step wizard: pick months, write a filter,
                optionally add a Lucene search, choose where to write, review
                the count estimate, submit.
              </Typography>
              <Button component={RouterLink} to="/exports/new" variant="contained">
                Create a Dataset
              </Button>
            </Paper>
            <Paper variant="outlined" sx={{ p: 3, flex: 1 }}>
              <Typography variant="h6">Your Datasets</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                Track progress, cancel running jobs, grab presigned download
                URLs once complete.
              </Typography>
              <Button component={RouterLink} to="/exports" variant="outlined">
                Your Datasets
              </Button>
            </Paper>
          </Stack>
        </>
      )}
    </Container>
  );
}
